import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createClient } from '@supabase/supabase-js';

/**
 * Isolated Mentors student auth — never load public.users / CRM AuthContext.
 * Authorization relies on app_metadata.account_type === 'mentors_student'.
 */

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const studentSupabase =
  supabaseUrl && supabaseAnonKey
    ? createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          storageKey: 'pm-student-auth',
        },
      })
    : null;

const StudentAuthContext = createContext({
  student: null,
  session: null,
  loading: true,
  signIn: async () => {},
  signOut: async () => {},
  refreshStudent: async () => {},
});

function isMentorsStudentJwt(session) {
  const accountType = session?.user?.app_metadata?.account_type;
  return accountType === 'mentors_student';
}

export function StudentAuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadStudent = useCallback(async (nextSession) => {
    if (!studentSupabase || !nextSession?.user?.id || !isMentorsStudentJwt(nextSession)) {
      setStudent(null);
      return null;
    }
    const { data, error } = await studentSupabase
      .from('mentors_users')
      .select('id, email, full_name, created_at')
      .eq('id', nextSession.user.id)
      .maybeSingle();
    if (error || !data) {
      setStudent(null);
      return null;
    }
    setStudent(data);
    return data;
  }, []);

  useEffect(() => {
    if (!studentSupabase) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;

    studentSupabase.auth.getSession().then(async ({ data }) => {
      if (cancelled) return;
      const s = data.session;
      if (s && !isMentorsStudentJwt(s)) {
        await studentSupabase.auth.signOut();
        setSession(null);
        setStudent(null);
      } else {
        setSession(s);
        await loadStudent(s);
      }
      setLoading(false);
    });

    const { data: sub } = studentSupabase.auth.onAuthStateChange(async (_event, next) => {
      if (next && !isMentorsStudentJwt(next)) {
        await studentSupabase.auth.signOut();
        setSession(null);
        setStudent(null);
        return;
      }
      setSession(next);
      await loadStudent(next);
    });

    return () => {
      cancelled = true;
      sub?.subscription?.unsubscribe();
    };
  }, [loadStudent]);

  const signIn = useCallback(async (email, password) => {
    if (!studentSupabase) throw new Error('Supabase is not configured');
    const { data, error } = await studentSupabase.auth.signInWithPassword({
      email: String(email || '').trim().toLowerCase(),
      password,
    });
    if (error) throw error;
    if (!isMentorsStudentJwt(data.session)) {
      await studentSupabase.auth.signOut();
      throw new Error('This account is not a Mentors student login.');
    }
    setSession(data.session);
    await loadStudent(data.session);
    return data.session;
  }, [loadStudent]);

  const signOut = useCallback(async () => {
    if (!studentSupabase) return;
    await studentSupabase.auth.signOut();
    setSession(null);
    setStudent(null);
  }, []);

  const refreshStudent = useCallback(async () => loadStudent(session), [loadStudent, session]);

  const value = useMemo(
    () => ({ student, session, loading, signIn, signOut, refreshStudent }),
    [student, session, loading, signIn, signOut, refreshStudent],
  );

  return <StudentAuthContext.Provider value={value}>{children}</StudentAuthContext.Provider>;
}

export function useStudentAuth() {
  return useContext(StudentAuthContext);
}
